import {
    Alert,
    AlertIcon,
    Box,
    Button,
    Checkbox,
    Collapse,
    Flex,
    Heading,
    HStack,
    Icon,
    IconButton,
    Modal,
    ModalCloseButton,
    ModalContent,
    ModalOverlay,
    Spinner,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Table,
    Tabs,
    Tag,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useToast,
    VStack,
} from '@chakra-ui/react';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { TbChevronDown, TbChevronUp, TbCircleCheck, TbPackage } from 'react-icons/tb';
import PartBayButton from '../components/PartBayButton';
import PartNumber from '../components/PartNumber';
import PartType from '../components/PartType';
import SourceBadge from '../components/SourceBadge';
import SupplierOrderInfoPopover from '../components/SupplierOrderInfoPopover';
import { formatPercentage, formatPrice } from '../functions';
import {
    OrderedPart,
    OrderedSection,
    useGetOrderDetail,
} from '../hooks/useGetOrderDetail';
import { useReceiveParts } from '../hooks/useReceiveParts';

export interface MarkReceivedOrderTab {
    orderId: string;
    roNumber: string;
    vehicle?: string;
    /** Parts selected on the board that should start checked in this RO tab. */
    initialPartIds: string[];
}

interface MarkReceivedModalProps {
    isOpen: boolean;
    onClose: () => void;
    orders: MarkReceivedOrderTab[];
    /**
     * Fired only when the server recorded at least one part. `updatedCount`
     * is the server's count and can be lower than `receivedPartIds.length`
     * (the API silently skips orders that predate the receiving opt-in).
     */
    onSuccess: (receivedPartIds: string[], updatedCount: number) => void;
}

const isPartReceivable = (part: OrderedPart): boolean => (
    !part.cancelled && !part.returned && !part.received
);

/** In-flight supplier sections only — cancelled/rejected orders have nothing arriving. */
const getReceivableSections = (sections: OrderedSection[]): OrderedSection[] => (
    sections.filter((section) => (
        section.status === 'ordered' || section.status === 'confirmed'
    ))
);

const receiveCheckboxSx = {
    '&[data-checked]': {
        bg: '#0071E3',
        borderColor: '#0071E3',
    },
    '&[data-checked]:hover': {
        bg: '#005BC4',
        borderColor: '#005BC4',
    },
} as const;

const MarkReceivedModal = ({
    isOpen,
    onClose,
    orders,
    onSuccess,
}: MarkReceivedModalProps) => {
    // Board surface: the Delivery Board manages its own optimistic
    // just-received rows, so don't invalidate the board queries from here.
    const receiveMutation = useReceiveParts({ invalidateBoard: false });
    const toast = useToast();
    const [selectedPartIds, setSelectedPartIds] = useState<Set<string>>(() => new Set());
    /** All known part ids per RO — seeded from the board, expanded as each order panel loads. */
    const [partsByOrderId, setPartsByOrderId] = useState<Map<string, Set<string>>>(() => new Map());
    const [tabIndex, setTabIndex] = useState(0);

    // Seed selection + per-RO part index whenever the modal opens for a new set of orders.
    useEffect(() => {
        if (!isOpen) return;
        const initial = new Set<string>();
        const byOrder = new Map<string, Set<string>>();
        orders.forEach((order) => {
            const partIds = new Set(order.initialPartIds);
            byOrder.set(order.orderId, partIds);
            order.initialPartIds.forEach((id) => initial.add(id));
        });
        setSelectedPartIds(initial);
        setPartsByOrderId(byOrder);
        setTabIndex(0);
    }, [isOpen, orders]);

    const registerOrderParts = useCallback((orderId: string, partIds: string[]) => {
        if (partIds.length === 0) return;
        setPartsByOrderId((prev) => {
            const existing = prev.get(orderId);
            const nextSet = new Set(existing);
            let changed = false;
            partIds.forEach((id) => {
                if (!nextSet.has(id)) {
                    nextSet.add(id);
                    changed = true;
                }
            });
            if (!changed && existing) return prev;
            const next = new Map(prev);
            next.set(orderId, nextSet);
            return next;
        });
    }, []);

    const selectedCountByOrder = useMemo(() => {
        const counts = new Map<string, number>();
        partsByOrderId.forEach((partIds, orderId) => {
            let count = 0;
            partIds.forEach((id) => {
                if (selectedPartIds.has(id)) count += 1;
            });
            counts.set(orderId, count);
        });
        return counts;
    }, [partsByOrderId, selectedPartIds]);

    const togglePart = useCallback((partId: string) => {
        setSelectedPartIds((prev) => {
            const next = new Set(prev);
            if (next.has(partId)) next.delete(partId);
            else next.add(partId);
            return next;
        });
    }, []);

    const toggleParts = useCallback((partIds: string[], selectAll: boolean) => {
        setSelectedPartIds((prev) => {
            const next = new Set(prev);
            partIds.forEach((id) => {
                if (selectAll) next.add(id);
                else next.delete(id);
            });
            return next;
        });
    }, []);

    const handleConfirm = () => {
        const orderPartIds = Array.from(selectedPartIds);
        if (orderPartIds.length === 0) return;
        receiveMutation.mutate(
            { orderPartIds, received: true },
            {
                onSuccess: (data) => {
                    // The API silently skips orders that predate the receiving
                    // opt-in date, so updatedCount can be 0.
                    if (data.updatedCount === 0) {
                        toast({
                            title: 'No parts were marked received',
                            description: 'The selected order(s) predate when receiving was enabled for your organization, so their parts can\'t be marked received.',
                            status: 'warning',
                            duration: 5000,
                            isClosable: true,
                        });
                        onClose();
                        return;
                    }
                    onSuccess(orderPartIds, data.updatedCount);
                    onClose();
                },
                // Leave the modal open on failure so the selection survives a retry.
                onError: () => {
                    toast({
                        title: 'Failed to mark parts received',
                        status: 'error',
                        duration: 3000,
                        isClosable: true,
                    });
                },
            },
        );
    };

    const selectedCount = selectedPartIds.size;

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
            <ModalOverlay />
            <ModalContent
                maxW="90vw"
                h="85vh"
                borderRadius="xl"
                display="flex"
                flexDirection="column"
                overflow="hidden"
            >
                <ModalCloseButton />

                <Box bg="gray.50" flexShrink={0} borderBottom="1px solid" borderColor="gray.200">
                    <Flex px={6} pt={4} pb={3} align="flex-start" justify="space-between" pr={12}>
                        <VStack spacing={1} align="flex-start" minW={0}>
                            <HStack spacing={2} align="center">
                                <Icon as={TbPackage as React.ElementType} boxSize={5} color="#0071E3" />
                                <Heading size="md" color="gray.900" lineHeight="1.25">
                                    Mark Parts Received
                                </Heading>
                            </HStack>
                            <Text fontSize="sm" color="gray.500">
                                Review parts on {orders.length === 1 ? 'this RO' : 'these ROs'} and confirm what arrived.
                            </Text>
                        </VStack>
                    </Flex>
                </Box>

                <Tabs
                    index={tabIndex}
                    onChange={setTabIndex}
                    variant="unstyled"
                    display="flex"
                    flexDirection="column"
                    flex={1}
                    minH={0}
                    isLazy
                >
                    {orders.length > 1 && (
                        <Box
                            flexShrink={0}
                            borderBottomWidth="1.5px"
                            borderBottomStyle="solid"
                            borderBottomColor="gray.200"
                            px={6}
                        >
                            <TabList
                                border="0"
                                borderBottom="0"
                                gap={0}
                                overflowX="auto"
                                maxW="100%"
                                mb="-1.5px"
                            >
                                {orders.map((order) => {
                                    const orderSelectedCount = selectedCountByOrder.get(order.orderId) ?? 0;
                                    return (
                                        <Tab
                                            key={order.orderId}
                                            fontWeight="medium"
                                            fontSize="sm"
                                            color="gray.500"
                                            bg="transparent"
                                            border="0"
                                            borderRadius={0}
                                            py={2}
                                            px={3}
                                            whiteSpace="nowrap"
                                            flexShrink={0}
                                            position="relative"
                                            _hover={{ color: 'gray.700' }}
                                            _selected={{
                                                color: 'gray.800',
                                                boxShadow: 'none',
                                            }}
                                            sx={{
                                                // Pseudo-element underline — Chakra Tab border resets
                                                // were swallowing borderBottom in this modal.
                                                '&[aria-selected=true]::after': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    height: '1.5px',
                                                    backgroundColor: 'var(--chakra-colors-gray-500)',
                                                },
                                            }}
                                        >
                                            <HStack spacing={0}>
                                                <Text>RO {order.roNumber}</Text>
                                                {orderSelectedCount > 0 && (
                                                    <Flex
                                                        ml={1.5}
                                                        bg="gray.100"
                                                        borderRadius="lg"
                                                        h="18px"
                                                        minW="18px"
                                                        px="5px"
                                                        align="center"
                                                        justify="center"
                                                        flexShrink={0}
                                                    >
                                                        <Text
                                                            fontSize="11px"
                                                            fontWeight="bold"
                                                            lineHeight="1"
                                                            color="gray.800"
                                                        >
                                                            {orderSelectedCount}
                                                        </Text>
                                                    </Flex>
                                                )}
                                            </HStack>
                                        </Tab>
                                    );
                                })}
                            </TabList>
                        </Box>
                    )}

                    <TabPanels flex={1} minH={0} overflow="hidden">
                        {orders.map((order) => (
                            <TabPanel
                                key={order.orderId}
                                h="100%"
                                p={0}
                                display="flex"
                                flexDirection="column"
                                overflow="hidden"
                            >
                                <MarkReceivedOrderPanel
                                    orderId={order.orderId}
                                    roNumber={order.roNumber}
                                    vehicle={order.vehicle}
                                    selectedPartIds={selectedPartIds}
                                    onTogglePart={togglePart}
                                    onToggleParts={toggleParts}
                                    onRegisterParts={registerOrderParts}
                                    showRoHeader={orders.length === 1}
                                />
                            </TabPanel>
                        ))}
                    </TabPanels>
                </Tabs>

                <Flex
                    px={6}
                    py={4}
                    borderTop="1px solid"
                    borderColor="gray.200"
                    justify="flex-end"
                    align="center"
                    gap={3}
                    flexShrink={0}
                    bg="white"
                >
                    <Text fontSize="sm" color="gray.500" mr="auto">
                        {selectedCount === 0
                            ? 'Select parts to mark received'
                            : `${selectedCount} part${selectedCount === 1 ? '' : 's'} selected`}
                    </Text>
                    <Button
                        size="md"
                        variant="outline"
                        borderColor="gray.300"
                        color="gray.600"
                        onClick={onClose}
                        isDisabled={receiveMutation.isPending}
                    >
                        Cancel
                    </Button>
                    <PartBayButton
                        text={selectedCount > 0 ? `Mark ${selectedCount} Received` : 'Mark Received'}
                        size="md"
                        colorScheme="partbay-blue"
                        variant="solid"
                        onClick={handleConfirm}
                        isLoading={receiveMutation.isPending}
                        isDisabled={selectedCount === 0}
                    />
                </Flex>
            </ModalContent>
        </Modal>
    );
};

interface MarkReceivedOrderPanelProps {
    orderId: string;
    roNumber: string;
    vehicle?: string;
    selectedPartIds: Set<string>;
    onTogglePart: (partId: string) => void;
    onToggleParts: (partIds: string[], selectAll: boolean) => void;
    onRegisterParts: (orderId: string, partIds: string[]) => void;
    showRoHeader: boolean;
}

const MarkReceivedOrderPanel = ({
    orderId,
    roNumber,
    vehicle,
    selectedPartIds,
    onTogglePart,
    onToggleParts,
    onRegisterParts,
    showRoHeader,
}: MarkReceivedOrderPanelProps) => {
    const { data, isLoading, isError, refetch } = useGetOrderDetail(orderId, { enabled: !!orderId });

    const sections = useMemo(() => {
        const allTab = data?.ordered.find((tab) => tab.key === 'all') ?? data?.ordered[0];
        return getReceivableSections(allTab?.sections ?? []);
    }, [data]);

    // Keep tab counts accurate as the user selects additional parts on this RO.
    useEffect(() => {
        const partIds = sections.flatMap((section) => (
            section.parts.filter(isPartReceivable).map((part) => part.partId)
        ));
        onRegisterParts(orderId, partIds);
    }, [orderId, sections, onRegisterParts]);

    if (isLoading) {
        return (
            <Flex flex={1} align="center" justify="center">
                <Spinner size="lg" color="#0071E3" />
            </Flex>
        );
    }

    if (isError || !data) {
        return (
            <Flex flex={1} align="center" justify="center" p={6}>
                <Alert status="error" borderRadius="md" maxW="480px">
                    <AlertIcon />
                    <VStack align="flex-start" spacing={2}>
                        <Text fontSize="sm">Failed to load RO {roNumber}.</Text>
                        <PartBayButton text="Retry" size="xs" colorScheme="partbay-blue" variant="outline" onClick={() => refetch()} />
                    </VStack>
                </Alert>
            </Flex>
        );
    }

    return (
        <Box flex={1} overflowY="auto" px={6} py={4}>
            {showRoHeader && (
                <HStack spacing={2} mb={4} align="baseline">
                    <Heading size="sm" color="gray.800">RO {roNumber}</Heading>
                    {(vehicle || data.vehicleName) && (
                        <Text fontSize="sm" color="gray.500">
                            {vehicle || data.vehicleName}
                        </Text>
                    )}
                </HStack>
            )}

            {sections.length === 0 ? (
                <Flex
                    align="center"
                    justify="center"
                    border="1px dashed"
                    borderColor="gray.200"
                    borderRadius="lg"
                    py={12}
                >
                    <Text fontSize="sm" color="gray.500">
                        No in-flight supplier orders on this RO.
                    </Text>
                </Flex>
            ) : (
                <VStack spacing={4} align="stretch">
                    {sections.map((section) => (
                        <SupplierReceiveSection
                            key={section.key}
                            section={section}
                            vin={data.vin}
                            selectedPartIds={selectedPartIds}
                            onTogglePart={onTogglePart}
                            onToggleParts={onToggleParts}
                        />
                    ))}
                </VStack>
            )}
        </Box>
    );
};

interface SupplierReceiveSectionProps {
    section: OrderedSection;
    vin: string;
    selectedPartIds: Set<string>;
    onTogglePart: (partId: string) => void;
    onToggleParts: (partIds: string[], selectAll: boolean) => void;
}

const SupplierReceiveSection = ({
    section,
    vin,
    selectedPartIds,
    onTogglePart,
    onToggleParts,
}: SupplierReceiveSectionProps) => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const receivableParts = useMemo(
        () => section.parts.filter(isPartReceivable),
        [section.parts],
    );
    const selectedInSection = receivableParts.filter((part) => selectedPartIds.has(part.partId));
    const allSelected = receivableParts.length > 0
        && receivableParts.every((part) => selectedPartIds.has(part.partId));

    const statusLabel = section.received
        ? 'Received'
        : section.status === 'confirmed'
            ? 'Confirmed'
            : section.status === 'ordered'
                ? 'Ordered'
                : null;
    const statusColor = section.received
        ? 'green'
        : section.status === 'confirmed'
            ? 'purple'
            : 'teal';

    return (
        <Box
            border="1px solid"
            borderColor="gray.200"
            borderRadius="lg"
            overflow="hidden"
            bg="white"
        >
            {/* Mirrors OrderedTabs supplier header: badge → name → contact popover / status · date */}
            <Flex px={6} pt={5} pb={4} justify="space-between" align="center" gap={3}>
                <HStack spacing={3} align="center" minW={0} flex={1}>
                    {section.source && (
                        <SourceBadge source={section.source} />
                    )}
                    <Text fontSize="lg" fontWeight="semibold" color="gray.800" noOfLines={1}>
                        {section.title}
                    </Text>
                    {section.displayId && (
                        <Text
                            fontSize="sm"
                            color="gray.500"
                            fontWeight="medium"
                            bg="gray.50"
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="md"
                            px={2}
                            py={0.5}
                            lineHeight="1.2"
                            flexShrink={0}
                        >
                            {section.displayId}
                        </Text>
                    )}
                    <SupplierOrderInfoPopover
                        label="Contact Info"
                        phone={section.phoneNumber}
                        email={section.email}
                    />
                </HStack>
                <HStack spacing={3} flexShrink={0}>
                    <HStack spacing={2}>
                        {statusLabel && (
                            <>
                                <Tag
                                    size="sm"
                                    colorScheme={statusColor}
                                    variant="subtle"
                                    borderRadius="md"
                                    fontWeight="medium"
                                    fontSize="xs"
                                >
                                    {statusLabel}
                                </Tag>
                                <Text fontSize="sm" color="gray.600" fontWeight="semibold">•</Text>
                            </>
                        )}
                        <Text fontSize="sm" color="gray.600" fontWeight="semibold">
                            {new Date(section.timePlaced).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                            })} • {new Date(section.timePlaced).toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit',
                            })}
                        </Text>
                    </HStack>
                    <IconButton
                        aria-label={isCollapsed ? `Expand ${section.title}` : `Collapse ${section.title}`}
                        icon={<Icon as={(isCollapsed ? TbChevronDown : TbChevronUp) as React.ElementType} boxSize={4} />}
                        size="sm"
                        variant="ghost"
                        color="gray.600"
                        onClick={() => setIsCollapsed((prev) => !prev)}
                    />
                </HStack>
            </Flex>

            <Collapse in={!isCollapsed} animateOpacity>
                <Box borderTop="1px solid" borderColor="gray.200" overflowX="auto">
                    <Table
                        variant="simple"
                        size="sm"
                        w="100%"
                        minW="780px"
                        sx={{
                            tableLayout: 'fixed',
                            'th, td': { verticalAlign: 'middle' },
                            'th:first-of-type, td:first-of-type': { pl: 6 },
                            'th:last-of-type, td:last-of-type': { pr: 6 },
                        }}
                    >
                        <Thead>
                            <Tr bg="gray.50">
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} px={4} w="50px">
                                    <Checkbox
                                        isChecked={allSelected}
                                        isIndeterminate={!allSelected && selectedInSection.length > 0}
                                        isDisabled={receivableParts.length === 0}
                                        onChange={() => onToggleParts(
                                            receivableParts.map((part) => part.partId),
                                            !allSelected,
                                        )}
                                        size="md"
                                        sx={receiveCheckboxSx}
                                    />
                                </Th>
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} px={4} w="220px">
                                    PART NAME
                                </Th>
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} px={3} w="130px">
                                    OE PART #
                                </Th>
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} px={3} w="70px" textAlign="center">
                                    QTY
                                </Th>
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} px={3} w="120px">
                                    DELIVERY ETA
                                </Th>
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} px={3} w="100px" isNumeric>
                                    LIST
                                </Th>
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} px={3} w="100px" isNumeric>
                                    BUY
                                </Th>
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} px={3} w="100px" isNumeric>
                                    MARGIN
                                </Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {section.parts.map((part) => {
                                const inactive = !!part.cancelled || !!part.returned;
                                const receivable = isPartReceivable(part);
                                const isSelected = selectedPartIds.has(part.partId);
                                const textColor = part.cancelled
                                    ? 'red.500'
                                    : part.returned
                                        ? 'blue.500'
                                        : part.received
                                            ? 'gray.500'
                                            : 'gray.900';

                                return (
                                    <Fragment key={part.partId}>
                                        <Tr
                                            bg={isSelected ? 'blue.50' : undefined}
                                            opacity={inactive ? 0.55 : 1}
                                            _hover={{ bg: isSelected ? 'blue.50' : 'gray.25' }}
                                            cursor={receivable ? 'pointer' : 'default'}
                                            onClick={() => {
                                                if (receivable) onTogglePart(part.partId);
                                            }}
                                        >
                                            <Td py={3} px={3} w="44px" onClick={(e) => e.stopPropagation()}>
                                                {part.received ? (
                                                    <Icon
                                                        as={TbCircleCheck as React.ElementType}
                                                        boxSize={4}
                                                        color="green.500"
                                                    />
                                                ) : (
                                                    <Checkbox
                                                        isChecked={isSelected}
                                                        isDisabled={!receivable}
                                                        onChange={() => onTogglePart(part.partId)}
                                                        size="md"
                                                        sx={receiveCheckboxSx}
                                                    />
                                                )}
                                            </Td>
                                            <Td py={3} px={3}>
                                                <VStack align="flex-start" spacing={1}>
                                                    <HStack spacing={1.5}>
                                                        <Text
                                                            fontSize="sm"
                                                            fontWeight="medium"
                                                            color={textColor}
                                                            textDecoration={inactive || part.received ? 'line-through' : undefined}
                                                        >
                                                            {part.name}
                                                        </Text>
                                                        {part.received && (
                                                            <Tag size="sm" colorScheme="green" variant="subtle" borderRadius="md" fontSize="xs">
                                                                Received
                                                            </Tag>
                                                        )}
                                                    </HStack>
                                                    {part.type ? <PartType partType={part.type} /> : null}
                                                </VStack>
                                            </Td>
                                            <Td py={3} px={3}>
                                                <Box
                                                    textDecoration={inactive || part.received ? 'line-through' : undefined}
                                                    color={part.cancelled ? 'red.500' : part.returned ? 'blue.500' : undefined}
                                                >
                                                    <PartNumber
                                                        partNumber={part.partNumber}
                                                        correctedPartNumber={part.correctedPartNumber}
                                                        vin={vin}
                                                    />
                                                </Box>
                                            </Td>
                                            <Td py={3} px={3} textAlign="center">
                                                <Text fontSize="sm" color={textColor}>
                                                    {part.quantity ?? '—'}
                                                </Text>
                                            </Td>
                                            <Td py={3} px={3}>
                                                {part.correctedDelivery && part.correctedDelivery !== part.delivery ? (
                                                    <VStack align="flex-start" spacing={0}>
                                                        <Text fontSize="sm" color="gray.500" textDecoration="line-through">
                                                            {part.delivery || '—'}
                                                        </Text>
                                                        <Text fontSize="sm" color="orange.500" fontWeight="medium">
                                                            {part.correctedDelivery}
                                                        </Text>
                                                    </VStack>
                                                ) : (
                                                    <Text fontSize="sm" color={textColor}>
                                                        {part.delivery || '—'}
                                                    </Text>
                                                )}
                                            </Td>
                                            <Td py={3} px={3} isNumeric>
                                                <Text fontSize="sm" fontWeight="medium" color={textColor}>
                                                    {part.listPrice ? formatPrice(part.listPrice) : '—'}
                                                </Text>
                                            </Td>
                                            <Td py={3} px={3} isNumeric>
                                                <Text fontSize="sm" fontWeight="medium" color={textColor}>
                                                    {part.buyPrice ? formatPrice(part.buyPrice) : '—'}
                                                </Text>
                                            </Td>
                                            <Td py={3} px={3} isNumeric>
                                                <Text fontSize="sm" color={textColor}>
                                                    {formatPercentage(part.grossMargin)}
                                                </Text>
                                            </Td>
                                        </Tr>
                                    </Fragment>
                                );
                            })}
                        </Tbody>
                    </Table>
                </Box>
            </Collapse>
        </Box>
    );
};

export default MarkReceivedModal;
